import axiosInstance from '@/libs/axiosInstance';
import type { CodePattern } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

/** What the brand is told before a format is applied to codes already issued. */
export interface DiscountCodeMigrationPreview {
  renamed: number;
  kept: number;
  alreadyMatching: number;
  needsName: number;
  discountOff: number;
  hasGeneratedCodes: boolean;
  transitionDays: number;
  running: { migrationId: string; startedAt: string } | null;
}

interface DiscountCodeMigrationPreviewResponse {
  success: boolean;
  code: string;
  message: string;
  data: DiscountCodeMigrationPreview;
}

/**
 * Keyed on the pattern itself, not on the program alone: the preview answers a
 * question about one candidate format, so switching parts in the builder must
 * not read a count computed for the previous one.
 */
export const discountCodeMigrationPreviewKey = (programId: string, codePattern: CodePattern) =>
  ['programs', programId, 'discount-code-migration-preview', codePattern] as const;

/**
 * The format travels as one opaque, fully encoded value. Axios's default
 * serializer leaves `[`, `]`, `:` and `,` raw, and the API reads its query with
 * `qs`, which then hands the controller a list of fragments rather than a format.
 */
export const discountCodeMigrationPreviewQuery = (codePattern: CodePattern) =>
  `codePattern=${encodeURIComponent(JSON.stringify(codePattern))}`;

export const useDiscountCodeMigrationPreview = (
  programId: string,
  codePattern: CodePattern,
  { enabled }: { enabled: boolean }
) =>
  useQuery({
    queryKey: discountCodeMigrationPreviewKey(programId, codePattern),
    queryFn: async (): Promise<DiscountCodeMigrationPreview> => {
      const response = await axiosInstance.get<DiscountCodeMigrationPreviewResponse>(
        `/dashboard/programs/${programId}/discount-codes/migration-preview?${discountCodeMigrationPreviewQuery(
          codePattern
        )}`
      );
      return response.data.data;
    },
    enabled: enabled && Boolean(programId),
  });
